import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import { toast } from 'react-hot-toast';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const TestLectureProcessor = () => {
  const [transcript, setTranscript] = useState('');
  const [topic, setTopic] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedContent, setProcessedContent] = useState(null);
  const [error, setError] = useState(null);
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();

  // Theme-based styles
  const styles = {
    container: {
      backgroundColor: theme.background,
      color: theme.text
    },
    card: {
      backgroundColor: theme.background,
      borderColor: theme.border
    },
    heading: {
      color: theme.text
    },
    label: {
      color: theme.text
    },
    input: {
      backgroundColor: theme.background,
      color: theme.text,
      borderColor: theme.border
    },
    button: {
      backgroundColor: theme.primary,
      color: theme.text
    },
    buttonHover: {
      backgroundColor: theme.primaryHover
    },
    error: {
      backgroundColor: theme.error + '20', // Add transparency
      borderColor: theme.error,
      color: theme.error
    },
    code: {
      backgroundColor: theme.secondary,
      color: theme.text
    },
    placeholderText: {
      color: theme.accent
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!transcript.trim()) {
      setError('Please enter a transcript');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/test-process-transcript',
        {
          method: 'POST',
          body: JSON.stringify({
            transcript,
            topic: topic || 'General Topic'
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process transcript');
      }

      const data = await response.json();
      setProcessedContent(data);
      toast.success('Transcript processed successfully');
    } catch (err) {
      console.error('Error processing transcript:', err);
      setError(err.message);
      toast.error('Failed to process transcript');
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper function to normalize LaTeX delimiters
  const normalizeLatexDelimiters = (content) => {
    if (!content) return '';
    
    // First fix any remaining double backslashes in LaTeX commands
    let processed = content;
    
    // Replace excessive backslashes in common LaTeX commands
    processed = processed.replace(/\\\\([a-zA-Z]+)/g, '\\$1');
    
    // Common LaTeX commands that might need fixing
    const commonCommands = ['alpha', 'beta', 'gamma', 'delta', 'epsilon', 'theta', 'lambda', 'pi', 'sigma', 
                           'frac', 'sqrt', 'int', 'sum', 'prod', 'lim', 'inf'];
    
    // Fix double backslashes before common commands
    commonCommands.forEach(cmd => {
      const regExp = new RegExp(`\\\\+${cmd}`, 'g');
      processed = processed.replace(regExp, `\\${cmd}`);
    });
    
    // Fix spacing around block LaTeX
    processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (match, equation) => {
      // Clean up equation content - replace multiple backslashes
      const trimmed = equation.trim()
        .replace(/\\{3,}/g, '\\')  // Replace 3+ backslashes with one
        .replace(/\\{2}/g, '\\');  // Replace 2 backslashes with one
      
      return `\n\n$$${trimmed}$$\n\n`;
    });
    
    // Fix spacing around inline LaTeX
    processed = processed.replace(/(\S)(\$[^\$\n]+?\$)(\S)/g, '$1 $2 $3');
    
    // Fix inline equations
    processed = processed.replace(/\$([^\$\n]+?)\$/g, (match, equation) => {
      // Clean up equation content - replace multiple backslashes
      const trimmed = equation.trim()
        .replace(/\\{3,}/g, '\\')  // Replace 3+ backslashes with one
        .replace(/\\{2}/g, '\\');  // Replace 2 backslashes with one
        
      return `$${trimmed}$`;
    });
    
    return processed;
  };

  const markdownComponents = {
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold my-4" style={styles.heading}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-bold my-3" style={styles.heading}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-bold my-2" style={styles.heading}>{children}</h3>
    ),
    p: ({ children }) => (
      <p className="my-3 leading-relaxed" style={styles.text}>{children}</p>
    ),
    strong: ({ children }) => (
      <strong className="font-bold" style={styles.text}>{children}</strong>
    ),
    code: ({ inline, children, className }) => {
      if (inline) {
        return <code className="px-1 py-0.5 rounded" style={styles.code}>{children}</code>;
      }
      return (
        <pre className="p-4 rounded-lg overflow-x-auto my-4" style={styles.code}>
          <code className={className}>{children}</code>
        </pre>
      );
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 theme-transition" style={styles.container}>
      <h1 className="text-3xl font-bold mb-6" style={styles.heading}>Lecture Processor Test</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="topic" className="block text-sm font-medium mb-1" style={styles.label}>
                Lecture Topic (optional)
              </label>
              <input
                type="text"
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full p-2 border rounded-md theme-transition"
                style={styles.input}
                placeholder="E.g., Calculus, Physics, Computer Science"
              />
            </div>
            
            <div>
              <label htmlFor="transcript" className="block text-sm font-medium mb-1" style={styles.label}>
                Lecture Transcript
              </label>
              <textarea
                id="transcript"
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                className="w-full p-2 border rounded-md h-64 theme-transition"
                style={styles.input}
                placeholder="Paste your lecture transcript here"
              />
            </div>
            
            <button
              type="submit"
              disabled={isProcessing}
              className={`px-4 py-2 rounded-md transition-all theme-transition ${
                isProcessing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={styles.button}
              onMouseOver={e => !isProcessing && (e.currentTarget.style.backgroundColor = theme.primaryHover)}
              onMouseOut={e => !isProcessing && (e.currentTarget.style.backgroundColor = theme.primary)}
            >
              {isProcessing ? 'Processing...' : 'Process Transcript'}
            </button>
          </form>
          
          {error && (
            <div className="mt-4 p-3 border rounded-md theme-transition" style={styles.error}>
              {error}
            </div>
          )}
        </div>
        
        <div>
          <div className="border rounded-md p-4 h-full overflow-auto theme-transition" style={styles.card}>
            <h2 className="text-xl font-bold mb-4" style={styles.heading}>Processed Result</h2>
            
            {processedContent ? (
              <div>
                <h3 className="text-lg font-bold mb-2" style={styles.heading}>{processedContent.title}</h3>
                
                <div className="prose max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex, rehypeRaw]}
                    components={markdownComponents}
                  >
                    {normalizeLatexDelimiters(processedContent.content)}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <p className="theme-transition" style={styles.placeholderText}>
                Process a transcript to see the formatted result with LaTeX and Markdown
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestLectureProcessor; 